import { atom } from 'jotai';

import { type ComplianceNotificationType } from './types';

/**
 * In-memory (not persisted) record of which (type, placement) pairs have
 * already reported a `viewed` impression this app session.
 *
 * Keyed on type AND pinned-ness, not type alone: a row is first shown pinned
 * (unread, above the list) and later shown unpinned (read, inline with the
 * inmails). Those are two placements, and both impressions are wanted. A
 * type-only guard swallowed the read-placement `is_pinned: false` fire after
 * the pinned mount had already recorded the type.
 *
 * `PinnedComplianceNotificationCard` also renders inside `NotificationsList`'s
 * virtualized `VList`, which unmounts and remounts rows as they scroll out of
 * and back into the visible window. A `useRef` guard local to the card resets
 * on every such remount, so it double-counts an impression on every scroll
 * past the row rather than firing once per placement. This atom lives above
 * the card instance, so the same (type, pinned) key survives the remount.
 *
 * Deliberately session-scoped rather than reset per popover open/close: doing
 * that correctly needs the popover's own mount lifecycle wired in as well,
 * which is a bigger change than this bug fix calls for. The trade-off is a
 * missed impression if the same variant is shown again in a later popover
 * open within the same session — undercounting, not the inflated count this
 * fixes.
 */
export type ComplianceNotificationViewedKey =
  `${ComplianceNotificationType}:${'pinned' | 'unpinned'}`;

export const complianceNotificationViewedKey = (
  type: ComplianceNotificationType,
  pinned: boolean
): ComplianceNotificationViewedKey => `${type}:${pinned ? 'pinned' : 'unpinned'}`;

export const viewedComplianceNotificationTypesAtom = atom<
  ReadonlySet<ComplianceNotificationViewedKey>
>(new Set<ComplianceNotificationViewedKey>());
